import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Layer } from '../../types/design';
import { getBaseAssetUrl } from './LayerRenderer';

interface PlaylistRendererProps {
    layer: Layer;
    isPlaying?: boolean;
    isTimelineOpen?: boolean;
    currentTime?: number;
    style?: React.CSSProperties;
    onClick?: (e: React.MouseEvent) => void;
    onMouseDown?: (e: React.MouseEvent) => void;
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onImageLoad?: () => void;
}
export const PlaylistRenderer: React.FC<PlaylistRendererProps> = ({
    layer,
    isPlaying = true,
    currentTime = 0,
    style,
    onClick,
    onMouseDown,
    onMouseEnter,
    onMouseLeave,
    onImageLoad
}) => {
    const contents = useMemo(() => {
        try {
            const data = JSON.parse(layer.content || '{}');
            return data?.contents || [];
        } catch (e) {
            console.error("Failed to parse playlist data", e);
            return [];
        }
    }, [layer.content]);

    const currentIndex = useMemo(() => {
        if (!contents.length) return 0;

        const totalDurationMs = contents.reduce((acc: number, item: any) => acc + (item.duration || 5), 0) * 1000;
        if (totalDurationMs <= 0) return 0;

        const timeInCycle = currentTime % totalDurationMs;
        let cumulative = 0;

        for (let i = 0; i < contents.length; i++) {
            const itemDuration = (contents[i].duration || 5) * 1000;
            if (timeInCycle < cumulative + itemDuration) {
                return i;
            }
            cumulative += itemDuration;
        }
        return 0;
    }, [contents, currentTime]);



    if (!contents.length) {
        return (
            <div
                id={layer.id}
                style={style}
                className={layer.className}
                onClick={onClick}
                onMouseDown={onMouseDown}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <div className="flex flex-col items-center justify-center w-full h-full p-4 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-black">
                    <span className="text-gray-500 font-medium">Empty Playlist</span>
                </div>
            </div>
        );
    }

    const currentItem = contents[currentIndex];
    if (!currentItem) return null;

    const mediaUrl = currentItem.signedUrl || currentItem.url;

    return (
        <div
            id={layer.id}
            style={style}
            className={layer.className}
            data-is-playlist="true"
            onClick={onClick}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <PlaylistMediaItem
                url={mediaUrl}
                fileType={currentItem.file_type}
                name={currentItem.name}
                isPlaying={isPlaying}
                onImageLoad={onImageLoad}
            />
        </div>
    );
};

interface PlaylistMediaItemProps {
    url: string;
    fileType: string;
    name?: string;
    isPlaying: boolean;
    onImageLoad?: () => void;
}

const PlaylistMediaItem = React.memo(({ url: incomingUrl, fileType, name, isPlaying, onImageLoad }: PlaylistMediaItemProps) => {
    const [displaySrc, setDisplaySrc] = useState(incomingUrl);
    const [hasError, setHasError] = useState(false);
    const lastBaseUrl = useRef(getBaseAssetUrl(incomingUrl));

    useEffect(() => {
        const newBaseUrl = getBaseAssetUrl(incomingUrl);
        if (newBaseUrl !== lastBaseUrl.current) {
            setDisplaySrc(incomingUrl);
            setHasError(false);
            lastBaseUrl.current = newBaseUrl;
        }
    }, [incomingUrl]);

    if (fileType?.startsWith('video/')) {
        return (
            <video
                src={displaySrc}
                crossOrigin="anonymous"
                autoPlay={isPlaying}
                muted
                playsInline
                draggable={false}
                onDragStart={e => e.preventDefault()}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
        );
    }

    return (
        <img
            src={hasError ? '/logo.png' : displaySrc}
            alt={name || 'Playlist item'}
            style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: hasError ? 'contain' : 'cover',
                padding: hasError ? '10px' : '0',
                opacity: hasError ? 0.3 : 1
            }}
            draggable={false}
            onDragStart={e => e.preventDefault()}
            crossOrigin="anonymous"
            onLoad={onImageLoad}
            onError={() => {
                if (hasError) return;
                setHasError(true);
            }}
        />
    );
});

PlaylistMediaItem.displayName = 'PlaylistMediaItem';
